<template>
  <div v-if="modifiedSalesDocument" class="grid mt-2 bg-white border-t">
    <div class="flex justify-end items-center px-4 py-2 gap-2">
      <BaseControlsButtonIcon
        v-if="mode === 'edit'"
        :tooltip-text="$t('common.tooltip.filterMaterialsByGroup')"
        @click="openFiltersModal">
        <IconFilters class="size-4" />
      </BaseControlsButtonIcon>

      <button
        v-if="mode === 'edit'"
        type="button"
        @click="addNewLine"
        class="px-2 py-1 font-normal rounded-md border border-gray-300">
        <span class="flex gap-2 justify-center items-center text-sm capitalize text-theme-500">
          <IconAddSmall />
          <p>{{ $t('common.actions.addNew') }}</p>
        </span>
      </button>
    </div>

    <!-- Filter Tags Display -->
    <div
      v-if="activeFilters && activeFilters.length > 0"
      class="flex flex-wrap gap-1.5 my-2 justify-center px-4">
      <div
        v-for="(item, index) in visibleTags"
        :key="index"
        class="inline-flex items-center p-1 m-1 text-xs font-medium text-center text-gray-500 transition-all bg-gray-200 border rounded-md shadow-sm h-fit">
        {{ item.name }}

        <IconXmark
          class="w-3.5 h-3.5 ml-auto cursor-pointer"
          @click="handleSelection(item)"></IconXmark>
      </div>
      <button
        v-if="activeFilters && activeFilters.length > maxVisibleTags"
        type="button"
        class="mx-1 text-xs hover:text-theme-700"
        @click="toggleVisibleTags">
        {{
          showMore
            ? $t('common.actions.showLess')
            : `${$t('common.actions.showMore')} (${remainingTags})`
        }}
      </button>
    </div>

    <div
      v-for="detail in displayLines"
      :key="detail.id"
      class="grid gap-12 px-4 py-6 border-b"
      :class="isHorizontalLayout ? 'grid-cols-[auto_1fr_auto]' : 'grid-cols-1'">
      <div class="flex justify-center items-center h-auto max-w-32">
        <img
          v-if="detail.material.image"
          :src="`data:image/jpeg;base64,${detail.material.image}`"
          alt="placeholder"
          class="object-cover rounded-md" />
        <img
          v-else
          src="@/assets/images/placeholder-image.webp"
          alt="placeholder"
          class="object-cover rounded-md" />
      </div>
      <div class="flex flex-col gap-4 justify-center items-start">
        <div
          class="flex gap-4 justify-start items-center"
          :class="isHorizontalLayout ? '' : 'flex-wrap '">
          <div class="max-w-40">
            <label class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
            <BaseTableCellSearchbox
              :error="validate('materialCode', detail.id)"
              v-model="detail.materialCode"
              :mode="mode"
              @update-search="onUpdateMaterialAlterCodesSearch(detail.materialCode, detail)"
              @clear-selection="clearItem(detail)" />
          </div>

          <div class="max-w-48">
            <label class="text-sm font-medium">{{ $t('salesDocuments.description') }}</label>
            <BaseTableCellSearchbox
              :error="validate('materialName', detail.id)"
              :mode="mode"
              v-model="detail.materialName"
              @update-search="onUpdateMaterialAlterCodesSearch(detail.materialCode, detail)"
              @clear-selection="clearItem(detail)" />
          </div>

          <BaseSelectDropdown
            name="warehouseDropdown"
            size="sm"
            flat
            :mode="mode"
            :label="$t('salesDocuments.headers.warehouse')"
            :items="dropdownWarehouses"
            :selected-item="{ value: detail.warehouseId ?? '', name: detail.warehouse?.name ?? '' }"
            show-error-as-tooltip
            :error="validate('warehouse.name', detail.id)"
            @update:selected="handleWarehouseSelection($event, detail)"
            class="w-40" />

          <div class="max-w-40">
            <label class="text-sm font-medium">{{ $t('salesDocuments.exists') }}</label>
            <BaseToggle
              :disable="mode === 'view'"
              class="flex flex-col justify-center text-sm"
              :name="`existsToggle-${detail.id}`"
              v-model="detail.exists"
              @change="inputUpdated('exists', detail)" />
          </div>

          <div class="max-w-40">
            <label class="text-sm font-medium">{{ $t('salesDocuments.notExists') }}</label>
            <BaseToggle
              :disable="mode === 'view'"
              class="flex flex-col justify-center text-sm"
              :name="`notExistsToggle-${detail.id}`"
              v-model="detail.notExists"
              @change="inputUpdated('notExists', detail)" />
          </div>
        </div>

        <div
          class="flex gap-4 justify-start items-center"
          :class="isHorizontalLayout ? '' : 'flex-wrap'">
          <!-- Dynamic Quantity Fields -->
          <div
            v-for="quantityField in getDynamicQuantityFields(
              detail,
              modifiedSalesDocument?.documentHeader?.documentType,
              measurementUnits
            )"
            :key="`qty-${detail.id}-${quantityField.unitIndex}`"
            class="max-w-40">
            <label class="text-sm font-medium">
              {{
                quantityField.unitIndex === 1
                  ? $t('salesDocuments.quantity')
                  : $t('salesDocuments.quantity') + ` ${quantityField.unitIndex}`
              }}
              {{ quantityField.label ? `(${quantityField.label})` : '' }}
            </label>
            <BaseInput
              type="number"
              color="base"
              icon="right"
              class="h-8 bg-white"
              :model-value="getQuantityValue(detail, quantityField.unitIndex)"
              show-error-as-tooltip
              :disabled="isQuantityInputDisabled(detail, quantityField.unitIndex)"
              :error="
                validate(
                  `quantity${quantityField.unitIndex === 1 ? '' : quantityField.unitIndex}`,
                  detail.id
                )
              "
              @update:model-value="
                (value) => {
                  setQuantityValue(detail, quantityField.unitIndex, Number(value) || 0);
                  inputUpdated('quantity', detail);
                }
              ">
            </BaseInput>
          </div>

          <div
            v-if="
              (modifiedSalesDocument?.documentHeader?.documentType?.totalMeasurementUnits || 1) > 1
            "
            class="max-w-40">
            <label class="text-sm font-medium">{{ $t('salesDocuments.totalQuantity') }}</label>
            <div
              class="flex items-center justify-end px-3 h-8 bg-gray-50 rounded-md text-sm font-medium text-gray-700 border">
              {{ detail.totalQuantity }}
            </div>
          </div>

          <div class="max-w-40">
            <label class="text-sm font-medium">{{ $t('salesDocuments.unitPrice') }}</label>
            <BaseInput
              type="number"
              color="base"
              icon="right"
              price
              class="h-8 bg-white"
              v-model="detail.unitPrice"
              show-error-as-tooltip
              :disabled="isUnitPriceDisabled(detail)"
              :error="validate('unitPrice', detail.id)"
              @update:model-value="inputUpdated('unitPrice', detail)">
              <template #icon-right>
                <div
                  class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                  {{ '€' }}
                </div>
              </template>
            </BaseInput>
          </div>

          <div class="max-w-40">
            <label class="text-sm font-medium">{{
              $t('salesDocuments.unitDiscountPercentage')
            }}</label>
            <BaseInput
              type="number"
              color="base"
              icon="right"
              price
              class="h-8 bg-white"
              :readonly="!detail.allowDiscount"
              :disabled="mode === 'view' || !detail.allowDiscount"
              :error="validate('percentageDiscount', detail.id)"
              v-model="detail.percentageDiscount"
              show-error-as-tooltip
              @update:model-value="inputUpdated('percentageDiscount', detail)">
            </BaseInput>
          </div>

          <div class="max-w-40">
            <label class="text-sm font-medium">{{ $t('salesDocuments.discountValue') }}</label>
            <BaseInput
              type="number"
              color="base"
              icon="right"
              price
              class="h-8 bg-white"
              :disabled="mode === 'view' || !detail.allowDiscount"
              :readonly="!detail.allowDiscount"
              :error="validate('unitDiscount', detail.id)"
              v-model="detail.unitDiscount"
              show-error-as-tooltip
              @update:model-value="inputUpdated('unitDiscount', detail)">
              <template #icon-right>
                <div
                  class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                  {{ '€' }}
                </div>
              </template>
            </BaseInput>
          </div>

          <div class="max-w-40">
            <label class="text-sm font-medium">{{ $t('salesDocuments.netTotal') }}</label>
            <BaseInput
              type="number"
              color="base"
              icon="right"
              price
              class="h-8 bg-white"
              :disabled="mode === 'view'"
              v-model="detail.netTotal"
              @update:model-value="inputUpdated('netTotal', detail)"
              show-error-as-tooltip>
              <template #icon-right>
                <div
                  class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                  {{ '€' }}
                </div>
              </template>
            </BaseInput>
          </div>

          <div class="max-w-40">
            <label class="text-sm font-medium">{{ $t('salesDocuments.vat') }}</label>
            <BaseInput
              type="number"
              color="base"
              icon="right"
              class="h-8 bg-white"
              readonly
              disabled
              price
              v-model="detail.lineVatTotal"
              show-error-as-tooltip>
            </BaseInput>
          </div>

          <div class="max-w-40">
            <label class="text-sm font-medium">{{ $t('salesDocuments.grossTotal') }}</label>
            <BaseInput
              type="number"
              color="base"
              icon="right"
              price
              class="h-8 bg-white"
              v-model="detail.grossTotal"
              show-error-as-tooltip
              :disabled="mode === 'view'"
              @update:model-value="inputUpdated('grossTotal', detail)">
              <template #icon-right>
                <div
                  class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                  {{ '€' }}
                </div>
              </template>
            </BaseInput>
          </div>
        </div>
      </div>
      <div
        class="flex items-start"
        :class="isHorizontalLayout ? 'justify-start' : ' row-start-1 justify-end'">
        <button
          v-if="detail.isModified && mode === 'edit'"
          class="p-1"
          @click="saveDocumentLine(detail)">
          <IconSave class="cursor-pointer size-4 hover:text-theme-600" />
        </button>
        <button
          class="p-1"
          @click="discardDocumentLine(detail)"
          v-if="detail.isModified && !isNewLine(detail) && mode === 'edit'">
          <IconXmark class="cursor-pointer size-4 hover:text-red-600" />
        </button>
        <div v-else class="flex justify-start items-center">
          <button
            v-if="!isNewLine(detail)"
            class="flex items-center justify-center p-[1px] rounded-full bg-slate-200 size-4"
            @click="onLineInfoClick(detail.materialId, detail.materialCode, detail.materialName)">
            <IconInfo class="rounded-full hover:text-theme-600 hove:bg-theme-100"></IconInfo>
          </button>
          <button
            @click="deleteLinePrompt(detail)"
            class="p-1"
            v-if="mode === 'edit' && (isNewLine(detail) || !detail.isModified)">
            <IconTrashOutline class="cursor-pointer size-4 hover:text-red-600" />
          </button>
        </div>
      </div>
    </div>
    <MaterialAlterCodesSearchResultsModal
      v-if="alterCodesModalVisible"
      :search-results="materialAlterCodesList"
      @cancel="onMaterialAlterCodesSearchCancel"
      @select="onMaterialAlterCodeSelected" />

    <BaseModalConfirm
      v-if="showDeleteConfirmPrompt"
      @confirm="deleteLine"
      @cancel="showDeleteConfirmPrompt = false" />

    <SalesDocumentsDetailsFiltersModal
      v-if="isFiltersModalOpen"
      :active-filters="activeFilters"
      @cancel="closeFiltersModal"
      @save="onFiltersSelected"
      @reset-filters="removeAllFilters" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { useLookupsStore } from '@/stores/lookups';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseTableCellSearchbox from '@/components/common/BaseTableCellSearchbox.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import MaterialAlterCodesSearchResultsModal from './MaterialAlterCodesSearchResultsModal.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconSave from '@/assets/icons/IconSave.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import IconAddSmall from '@/assets/icons/IconAddSmall.vue';
import IconInfo from '@/assets/icons/IconInfo.vue';
import IconFilters from '@/assets/icons/IconFilters.vue';
import BaseControlsButtonIcon from '@/components/common/BaseControlsButtonIcon.vue';
import SalesDocumentsDetailsFiltersModal from './SalesDocumentsDetailsFiltersModal.vue';

import type { MaterialAlterCodeForSalesDocument } from '@/types/Materials';
import type { DocumentDetail } from '@/types/Documents';
import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import type { FilterGroupItems } from '@/types/Groups';
import { useDebounceFn } from '@vueuse/core';
import { helpers, maxValue, minValue, required } from '@vuelidate/validators';
import BaseToggle from '@/components/common/BaseToggle.vue';

const props = defineProps<{ isHorizontalLayout: Boolean; mode: 'view' | 'edit' }>();

const { modifiedSalesDocument, customerPricingPolicy, clearDetails, activeFilters } =
  storeToRefs(useSalesDocumentsStore());
const {
  searchMaterialAlterCodes,
  getMaterialPropertiesFromCustomerPricingPolicy,
  getMaterialDiscountsFromCustomerPricingPolicy,
  getMaterialVatFactor,
  updateDocLines,
  priceOrQuantityChange,
  discountValueChange,
  discountPercentageChange,
  grossTotalChange,
  netTotalChange,
  initNewDocumentLine,
  saveDocumentLineInStore,
  docLineQuantityRule,
  docLineUnitPriceRule,
  priceZeroRule,
  discountNotMoreThanUnitPrice,
  updateExistsNotExistsFlags,
  setActiveFilters,
  removeFilter,
  clearAllFilters,
  processSiblingMaterialAlterCodes,
  initializeDynamicQuantities,
  getDynamicQuantityFields,
} = useSalesDocumentsStore();
const { dropdownWarehouses, warehouses, measurementUnits } = storeToRefs(useLookupsStore());
const { getWarehousesLookup, getMeasurementUnitsLookup } = useLookupsStore();

const toastStore = useToastStore();
const { t: $t } = useI18n();

function isNewLine(item: DocumentDetail): boolean {
  if (!newLineAdded.value) return false;
  return newDocumentLine.value?.id === item.id;
}

const alterCodesModalVisible = ref(false);
const showDeleteConfirmPrompt = ref(false);
const newLineAdded = ref(false);
const isFiltersModalOpen = ref(false);

const materialAlterCodesList = ref<MaterialAlterCodeForSalesDocument[]>([]);
const editedLine = ref<DocumentDetail>();
const docDetailLines = ref<DocumentDetail[]>([]);
const displayLines = computed(() => {
  return docDetailLines.value.filter((l) => l.recordMode != RecordMode.Deleted);
});

const newDocumentLine = ref<any>(null);

onMounted(async () => {
  warehouses.value = [];
  await getWarehousesLookup();
  await getMeasurementUnitsLookup();
  docDetailLines.value = deepCopy<DocumentDetail[]>(
    modifiedSalesDocument.value?.documentHeader.documentDetails?.filter((d) => !d.deleted) ?? []
  );
});

const emit = defineEmits(['materialInfo']);

function onLineInfoClick(materialId: string, code: string, name: string) {
  emit('materialInfo', { materialId, code, name });
}

// Helper functions for quantity field management
function getQuantityValue(detail: DocumentDetail, unitIndex: number): number {
  switch (unitIndex) {
    case 1:
      return detail.quantity || 0;
    case 2:
      return detail.quantity2 || 0;
    case 3:
      return detail.quantity3 || 0;
    default:
      return 0;
  }
}

function setQuantityValue(detail: DocumentDetail, unitIndex: number, value: number) {
  switch (unitIndex) {
    case 1:
      detail.quantity = value;
      break;
    case 2:
      detail.quantity2 = value;
      break;
    case 3:
      detail.quantity3 = value;
      break;
  }
}

function isQuantityInputDisabled(detail: DocumentDetail, unitIndex: number): boolean {
  if (props.mode === 'view') {
    return true;
  }
  switch (unitIndex) {
    case 1:
      return (
        !detail.measurementUnit1Id ||
        detail.measurementUnit1Id === '00000000-0000-0000-0000-000000000000'
      );
    case 2:
      return (
        !detail.measurementUnit2Id ||
        detail.measurementUnit2Id === '00000000-0000-0000-0000-000000000000'
      );
    case 3:
      return (
        !detail.measurementUnit3Id ||
        detail.measurementUnit3Id === '00000000-0000-0000-0000-000000000000'
      );
    default:
      return true;
  }
}

function isUnitPriceDisabled(detail: DocumentDetail) {
  const typeAllowManualPrices =
    !!modifiedSalesDocument.value?.documentHeader.documentType?.allowManualPrices;
  return (
    props.mode === 'view' ||
    !typeAllowManualPrices ||
    !detail.material ||
    !detail.material.allowManualPrice
  );
}

watch(
  () => modifiedSalesDocument.value?.documentHeader.documentDetails,
  (newDetails) => {
    if (!newDetails) return;
    const newDetailsCopy = deepCopy<DocumentDetail[]>(newDetails);
    if (!clearDetails.value) {
      // when on edit mode I save one line but there are more that have been modified, do not overwrite them.
      const localUnsaved = docDetailLines.value.filter(
        (l) => l.isModified && l.id !== editedLine.value?.id
      );
      const localUnsavedIds = localUnsaved.map((x) => x.id);

      // Merge unsaved changes into the new details
      for (const newDetail of newDetailsCopy) {
        if (localUnsavedIds.includes(newDetail.id)) {
          const index = newDetailsCopy.findIndex((x) => x.id === newDetail.id);
          newDetailsCopy[index] = {
            ...newDetailsCopy[index],
            ...localUnsaved.find((x) => x.id === newDetail.id),
          };
        }
      }
    }
    docDetailLines.value = deepCopy<DocumentDetail[]>(
      newDetailsCopy.filter((d) => !d.deleted) || []
    );
    // Keep the new document line, if applicable
    if (newDocumentLine.value)
      docDetailLines.value = [newDocumentLine.value, ...docDetailLines.value];

    clearDetails.value = false;
  },
  { deep: false }
);

async function onUpdateMaterialAlterCodesSearch(searchText: string, item: DocumentDetail) {
  editedLine.value = item;
  const results = await searchMaterialAlterCodes(
    searchText,
    undefined,
    activeFilters.value.map((f) => f.id)
  );
  if (results.length > 1) {
    materialAlterCodesList.value = results;
    alterCodesModalVisible.value = true;
  } else if (results.length === 1) {
    await onMaterialAlterCodeSelected(results[0]);
  } else {
    toastStore.attention({
      text: $t('messages.noResultsFor', { query: searchText }),
      hideTitle: true,
    });
    if (editedLine.value.materialAlterCode.alterCode) {
      editedLine.value.materialName = editedLine.value.materialAlterCode.alterCode.name;
      editedLine.value.materialCode = editedLine.value.materialAlterCode.alterCode.code;
    }
  }
}

async function onMaterialAlterCodeSelected(item: MaterialAlterCodeForSalesDocument) {
  if (!editedLine.value || !item) return;
  editedLine.value.materialAlterCode = item;
  editedLine.value.materialAlterCodeId = item.id ?? '';
  editedLine.value.materialCode = item.alterCode?.code ?? '';
  editedLine.value.materialName = item.alterCode?.name ?? '';
  editedLine.value.materialId = item.materialId ?? '';

  // Process sibling MaterialAlterCodes for measurement unit mapping
  if (item.allMaterialAlterCodes && item.allMaterialAlterCodes.length > 0) {
    editedLine.value = processSiblingMaterialAlterCodes(
      editedLine.value,
      item,
      Math.min(
        modifiedSalesDocument.value?.documentHeader?.documentType?.totalMeasurementUnits || 1,
        3
      )
    );
  } else {
    // Fallback to original logic if no sibling data
    editedLine.value.measurementUnit1Id = item.measurementUnitId ?? '';
  }

  if (editedLine.value && customerPricingPolicy.value) {
    editedLine.value = await getMaterialPropertiesFromCustomerPricingPolicy(editedLine.value);
  }
  if (editedLine.value)
    editedLine.value = await getMaterialDiscountsFromCustomerPricingPolicy(editedLine.value);
  if (editedLine.value) editedLine.value = await getMaterialVatFactor(editedLine.value);

  if (editedLine.value)
    docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);

  alterCodesModalVisible.value = false;
}

function onMaterialAlterCodesSearchCancel() {
  if (!editedLine.value) return;

  editedLine.value.materialName = editedLine.value.materialAlterCode?.alterCode?.name ?? '';
  editedLine.value.materialCode = editedLine.value.materialAlterCode?.alterCode?.code ?? '';

  alterCodesModalVisible.value = false;
}

function clearItem(line: DocumentDetail) {
  editedLine.value = line;
  if (editedLine.value) {
    editedLine.value.materialCode = '';
    editedLine.value.materialName = '';
    editedLine.value.materialAlterCodeId = '';
    docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
  }
}

function handleWarehouseSelection(event: SelectDropdownItem, item: DocumentDetail) {
  editedLine.value = item;
  if (editedLine.value) {
    const selectedWarehouse = warehouses.value.find((x) => x.id === event.value);
    if (selectedWarehouse) {
      editedLine.value.warehouseId = selectedWarehouse.id;
      editedLine.value.warehouse = selectedWarehouse;
    }
    docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
  }
}

function inputUpdated(
  header:
    | 'quantity'
    | 'unitPrice'
    | 'percentageDiscount'
    | 'unitDiscount'
    | 'grossTotal'
    | 'netTotal'
    | 'lineVatTotal'
    | 'exists'
    | 'notExists',
  item: DocumentDetail
) {
  editedLine.value = item;
  switch (header) {
    case 'quantity':
      editedLine.value = priceOrQuantityChange(editedLine.value, 'quantity');
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'unitPrice':
      editedLine.value = priceOrQuantityChange(editedLine.value, 'unitPrice');
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'unitDiscount':
      editedLine.value = discountValueChange(editedLine.value, validate(header, item.id));
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'percentageDiscount':
      editedLine.value = discountPercentageChange(editedLine.value, validate(header, item.id));
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'exists':
    case 'notExists':
      editedLine.value = updateExistsNotExistsFlags(
        editedLine.value,
        header as 'exists' | 'notExists'
      );
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'grossTotal':
      debounceGrossTotalChange();
      break;
    case 'netTotal':
      debounceNetTotalChange();
      break;
    default:
      break;
  }
}

const debounceGrossTotalChange = useDebounceFn(
  () => {
    if (editedLine.value) {
      editedLine.value = grossTotalChange(editedLine.value);
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
    }
  },
  700,
  { maxWait: 1200 }
);

const debounceNetTotalChange = useDebounceFn(
  () => {
    if (editedLine.value) {
      editedLine.value = netTotalChange(editedLine.value);
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
    }
  },
  700,
  { maxWait: 1200 }
);

function saveDocumentLine(line: DocumentDetail) {
  const rowIndex = docDetailLines.value.findIndex((row) => row.id === line.id);
  $v.value.$touch();
  if ($v.value.$invalid) {
    const rowErrors = $v.value.$errors[0]?.$response?.$errors[rowIndex];
    const hasErrors = rowErrors
      ? Object.values(rowErrors).some((value) => Array.isArray(value) && value.length > 0)
      : false;
    if (hasErrors) return;
  }
  editedLine.value = line;
  if (editedLine.value && editedLine.value.id == line.id) editedLine.value.isModified = false; // updates the memory object so as to control the toast notification
  saveDocumentLineInStore(line);

  if (newDocumentLine.value && newDocumentLine.value.id === line.id) {
    newDocumentLine.value = null;
    newLineAdded.value = false;
  }
}

function discardDocumentLine(line: DocumentDetail) {
  line.isModified = false;
  const index = docDetailLines.value.findIndex((x) => x.id === line.id);
  const lineBeforeChange = modifiedSalesDocument.value?.documentHeader.documentDetails?.find(
    (x) => x.id == line.id
  );
  if (index == undefined || index < 0 || !lineBeforeChange) return;
  docDetailLines.value[index] = {
    ...docDetailLines.value[index],
    ...lineBeforeChange,
  };
  editedLine.value = docDetailLines.value[index];
  $v.value.$touch();
}

function deleteLinePrompt(item: DocumentDetail) {
  editedLine.value = item;
  showDeleteConfirmPrompt.value = true;
}

function deleteLine() {
  showDeleteConfirmPrompt.value = false;
  if (!editedLine.value) return;

  if (!!newLineAdded.value && newDocumentLine.value.id == editedLine.value.id) {
    const index = docDetailLines.value.findIndex((x) => x.id === newDocumentLine.value.id);
    if (index == -1) return;
    docDetailLines.value.splice(index, 1);
    newLineAdded.value = false;
  } else {
    editedLine.value.recordMode = RecordMode.Deleted;
    saveDocumentLine(editedLine.value);
  }
}

function addNewLine() {
  if (newLineAdded.value) return;
  newLineAdded.value = true;
  newDocumentLine.value = initNewDocumentLine();
  docDetailLines.value = [newDocumentLine.value, ...docDetailLines.value];
  editedLine.value = newDocumentLine.value;
}

watch(
  () => editedLine.value,
  (newLine, oldLine) => {
    // If switching to a new line while the previous line was modified, prompt the user to save changes
    if (newLine?.id != oldLine?.id && oldLine?.isModified)
      toastStore.attention({
        text: $t('messages.saveLineChangesPrompt'),
        hideTitle: true,
      });

    // If switching from the new document line, restore it as the edited line
    if (
      newDocumentLine.value &&
      oldLine &&
      newLine &&
      newLine.id != oldLine.id &&
      oldLine.id === newDocumentLine.value.id
    )
      newDocumentLine.value = oldLine;
  },
  { deep: true }
);

// Initialize measurement unit fields when component mounts
onMounted(() => {
  if (docDetailLines.value.length > 0) {
    docDetailLines.value.forEach((detail) => {
      initializeDynamicQuantities(
        detail,
        modifiedSalesDocument.value?.documentHeader?.documentType
      );
    });
  }
});

function validate(field: string, rowId: string): string {
  const rowIndex = docDetailLines.value.findIndex((row) => row.id === rowId);
  const tableErrors = $v.value.$errors[0]?.$response?.$errors;
  if (!tableErrors || !tableErrors[rowIndex]) return '';
  const fieldErrors = tableErrors[rowIndex][field];
  return fieldErrors && fieldErrors.length > 0 ? fieldErrors[0].$message : '';
}

const tableModel = computed(() => ({
  lines: docDetailLines.value,
}));

const rulesTable = computed(() => ({
  lines: {
    $each: helpers.forEach({
      materialCode: {
        required: helpers.withMessage('Material selection is required', required),
      },
      materialName: {
        required: helpers.withMessage('Material selection is required', required),
      },
      totalQuantity: {
        docLineQuantityRule,
      },
      unitPrice: { docLineUnitPriceRule },
      grossTotal: { priceZeroRule },
      unitDiscount: {
        discountNotMoreThanUnitPrice,
      },
      percentageDiscount: {
        minValue: minValue(0),
        maxValue: maxValue(100),
      },
    }),
  },
}));

const $v = useVuelidate(rulesTable, tableModel);

// Filter modal functions
function openFiltersModal() {
  isFiltersModalOpen.value = true;
}

function closeFiltersModal() {
  isFiltersModalOpen.value = false;
}

function onFiltersSelected(selectedFilters: FilterGroupItems[]) {
  setActiveFilters(selectedFilters);
  closeFiltersModal();
}

function handleSelection(item: FilterGroupItems) {
  removeFilter(item.id);
}

function removeAllFilters() {
  clearAllFilters();
  closeFiltersModal();
}

// Filter tags display logic
const visibleTagsNum = ref(20);
const maxVisibleTags = ref(20);
const showMore = ref(false);

const visibleTags = computed(() => {
  if (!activeFilters.value) return [];
  return activeFilters.value.slice(0, visibleTagsNum.value);
});

const remainingTags = computed(() => {
  if (!activeFilters.value || activeFilters.value.length <= visibleTagsNum.value) return 0;
  return activeFilters.value.length - visibleTagsNum.value;
});

function toggleVisibleTags() {
  if (showMore.value) {
    showMore.value = false;
    visibleTagsNum.value = 20;
  } else {
    showMore.value = true;
    visibleTagsNum.value = activeFilters.value?.length || 0;
  }
}
</script>
